import {noop} from '@benchling/js-utils';
import Backbone from 'backbone';
import $ from 'jquery';

import * as analytics from 'analytics/Analytics';
import {AnalyticsEventMetadata} from 'analytics/AnalyticsTypes';
import PreviewModalTemplate from 'components/PreviewModalTemplate';
import Pagination from 'molbio/pdf/Pagination';
import notifications from 'notifications';
import * as popup from 'Popup';

export interface PreviewModalViewOptions extends Backbone.ViewOptions<Backbone.Model> {
  filename: string;
  landscape?: boolean;
  paginate: (css: string) => Pagination;
  trackMetadata?: AnalyticsEventMetadata;
}

class PreviewModalView extends Backbone.View<Backbone.Model> {
  currentPage!: number;
  filename!: string;
  landscape!: boolean;
  pagination!: Pagination;
  paginate!: (css: string) => Pagination;
  popup!: popup.Confirm;
  template!: (args?: {landscape: boolean}) => string;
  trackMetadata!: AnalyticsEventMetadata;

  constructor(options: PreviewModalViewOptions) {
    super(options);
  }

  static initClass(): void {
    this.prototype.template = PreviewModalTemplate;

    this.prototype.events = {
      // @ts-expect-error: Backbone events can be either the events hash or a method returning the events hash
      // The Backbone types do not know about this.
      'click .prev-btn': function (this: PreviewModalView) {
        return this.gotoPage(this.currentPage - 1);
      },
      'click .next-btn': function (this: PreviewModalView) {
        return this.gotoPage(this.currentPage + 1);
      },
      'click .first-btn': function (this: PreviewModalView) {
        return this.gotoPage(0);
      },
      'click .last-btn': function (this: PreviewModalView) {
        return this.gotoPage(this.pagination.pages.length - 1);
      },
    };
  }

  readyDownload(url: string): void {
    this.popup
      .$('.js-submit')
      .removeClass('disabled')
      .button('reset')
      .attr('target', '_blank')
      .attr('href', url)
      .click(() => {
        analytics.track('editor', 'downloaded_pdf', this.trackMetadata);
      });
  }

  gotoPage(pageIndex: number): JQuery {
    this.currentPage = pageIndex;

    const onFirst = pageIndex === 0;
    const onLast = pageIndex === this.pagination.pages.length - 1;

    this.$('.prev-btn').prop('disabled', onFirst);
    this.$('.first-btn').prop('disabled', onFirst);
    this.$('.next-btn').prop('disabled', onLast);
    this.$('.last-btn').prop('disabled', onLast);
    this.$('.page-count').text(`Page ${pageIndex + 1} of ${this.pagination.pages.length}`);

    this.$('svg.preview-container').empty();
    return this.$('svg.preview-container').append(this.pagination.pages[this.currentPage].svg);
  }

  override initialize(options: PreviewModalViewOptions): void {
    this.paginate = options.paginate;
    this.trackMetadata = options.trackMetadata || {};
    this.landscape = options.landscape || false;
    this.filename = options.filename;
  }

  showPreview(pagination: Pagination): void {
    this.pagination = pagination;
    this.gotoPage(0);
  }

  initiate(): JQueryPromise<string> {
    this.$('.status').text('Retrieving formatting...');
    return $.get('/static/build/svg.css')
      .then((css: string) => {
        css = css.split('\n').join('').split('\r').join('');
        this.$('.status').text('Creating pages...');
        const pagination = this.paginate(css);
        let message = 'Generating PDF...';
        if (pagination.truncated()) {
          message += `<br/>Note: For performance reasons, only the first ${pagination.pages.length} pages of
          the PDF will be generated. To see more of your alignment, consider adjusting the visibility settings
          or exporting multiple selections.`;
        }
        this.$('.status').html(message);
        this.showPreview(pagination);
        return this.pagination.toPDF(this.filename);
      })
      .fail(notifications.notifyError)
      .done((url: string) => {
        const $status = this.$('.status');
        $status.html('Done.');
        setTimeout(() => $status.slideUp(), 2000);
        this.readyDownload(url);
      });
  }

  override render(): this {
    this.$el.html(this.template({landscape: this.landscape}));

    this.$('.prev-btn').prop('disabled', true);
    this.$('.first-btn').prop('disabled', true);
    this.$('.next-btn').prop('disabled', true);
    this.$('.last-btn').prop('disabled', true);

    this.popup = new popup.Confirm({title: 'PDF Preview', msg: this.el})
      .linkChoice('Download', 'btn-af-success js-submit disabled', noop)
      .choice('Cancel', 'btn-af-default js-cancel');
    this.popup.show();

    this.popup.$('.js-submit').data({loadingText: 'Preparing PDF...'}).button('loading');
    this.popup.$('.js-submit').attr('data-test-component', 'PreviewModelView');
    this.popup.$('.js-submit').attr('data-test-element', 'downloadButton');

    this.initiate();
    return this;
  }
}
PreviewModalView.initClass();

export default PreviewModalView;
